import { getUserCenterPageRoute, UserCenterRoutes } from 'crypto-otc/constants/PageRoutes'
import { ElCheckbox, ElSwitch } from 'element-plus'
import { useI18n } from 'i18n-utils'
import { ApiStatus, InvestFinancialService } from 'network'
import { PlatformName, ProjectTheme } from 'platform-config'
import darkSuccessImg from 'platform-config/src/assets/common/financial/success-dark.png'
import successImg from 'platform-config/src/assets/common/financial/success.png'
import { InvestFinancialTab, InvestFinancialTabTypes, useAppInfoStore, useInvestFinancialDetail, useProjectTheme } from 'store'
import { DefineComponentProps } from 'utils'
import { CustomButton } from '../components/Button'
import { DialogHeader } from '../components/DialogHeader'
import { HocElDialog, HocElDialogFooterLayout } from '../hoc-element-components/HocElDialog'
import { useDialog } from '../stores/use-dialog'
import { UiUtils } from '../ui-utils/ui-utils'

export type SubscriptionFinancialProps = {
  visible: boolean
  onClose?: () => void
}

export const SubscriptionFinancial = defineComponent<SubscriptionFinancialProps>({
  props: DefineComponentProps<SubscriptionFinancialProps>(['visible', 'onClose']),
  setup(props) {
    const { dialogVisible, openDialog, closeDialog } = useDialog()
    const { dialogVisible: successDialogVisible, openDialog: openSuccessDialog, closeDialog: closeSuccessDialog } = useDialog()
    const checkedRiskTips = ref(false)
    const checkedProtocol = ref(false)

    const { t } = useI18n()
    const submitLoading = ref(false)
    const investFinancialDetail = useInvestFinancialDetail()
    const autoLoading = ref(false)
    const appInfo = useAppInfoStore()
    const themeStore = useProjectTheme()
    const { query } = useRoute()

    const successIcon = computed(() => {
      return themeStore.themeType === ProjectTheme.Dark ? darkSuccessImg : successImg
    })

    onBeforeMount(() => {
      if (props.visible) {
        openDialog()
      }
    })

    const isRiskTips = computed(() => {
      return investFinancialDetail.productDetail?.productType === InvestFinancialTabTypes[InvestFinancialTab.ChainEarn]
    })

    const onConfirm = async () => {
      submitLoading.value = true
      const res = await InvestFinancialService.buyFinance({
        subProductId: investFinancialDetail.activeSubProduct!.subProductId,
        amount: investFinancialDetail.amount,
        uvId: '', // 目前没有理财券
      })
      submitLoading.value = false
      if (res.status === ApiStatus.Success) {
        closeDialog()
        openSuccessDialog()
      }
    }

    const onClose = () => {
      closeDialog()
      props.onClose?.()
    }

    const isAutoPurchase = computed(() => {
      return investFinancialDetail.activeSubProduct?.autoBuyFinancial === 1
    })

    const isAutoPurchaseChecked = computed(() => {
      return investFinancialDetail.autoSubscriptionProductList.some((item) => item.subProductId === investFinancialDetail.activeSubProduct?.subProductId)
    })

    const autoPurchase = ref(isAutoPurchaseChecked.value)

    const onAutoPurchaseChange = async () => {
      if (autoLoading.value) return
      autoLoading.value = true
      const res = await InvestFinancialService.feAutoSubscription({
        subProductId: investFinancialDetail.activeSubProduct!.subProductId,
        autoBuy: isAutoPurchaseChecked.value ? 2 : 1,
      })
      autoLoading.value = false
      if (res.status === ApiStatus.Success) {
        autoPurchase.value = !autoPurchase.value
        investFinancialDetail.refreshProductDetail()
      }
    }

    const confirmDisabled = computed(() => {
      if (isRiskTips.value) {
        return !checkedRiskTips.value || !checkedRiskTips.value
      }
      return !checkedProtocol.value
    })

    const resetFormData = () => {
      const subProductId = query.subProductId as string
      investFinancialDetail.amount = ''
      investFinancialDetail.activeSubProduct = subProductId
        ? investFinancialDetail.subProductList.find((item) => item.subProductId === +subProductId)
        : investFinancialDetail.subProductList[0]
    }

    const onCloseSuccessDialog = () => {
      closeSuccessDialog()
      resetFormData()
    }

    const onQueryOrder = async () => {
      closeSuccessDialog()
      resetFormData()

      await UiUtils.router?.push({
        path: getUserCenterPageRoute(UserCenterRoutes.AssetFinance),
      })
      investFinancialDetail.$dispose()
    }

    const onOpenProtocol = () => {
      window.open(appInfo.appInfo?.financeAgreementUrl, '_blank')
    }

    return () => (
      <>
        <HocElDialog v-model={dialogVisible.value} width={400} placement="bottom" showClose={false} closeOnClickModal={false}>
          {{
            header: () => <DialogHeader onClose={onClose}>{t('申购')}</DialogHeader>,
            default: () => (
              <div>
                <div class="flex bg-[var(--colors-bg-main-page-card)] justify-between items-center rounded-[8px] h-[52px] py-4 text-[16px] font-[500] leading-[22px] text-[var(--colors-text-base)]">
                  <span>{t('申购数量')}</span>
                  <span>
                    {investFinancialDetail.amount.formatCommonMoney()} {investFinancialDetail.assetName}
                  </span>
                </div>
                {/* 条款 */}
                <div class={'privacy-terms mt-4'}>
                  <p class="text-[16px]">{t('请您阅读、理解并接受一下条款：')}</p>
                  {isRiskTips.value && (
                    <div class="mt-4">
                      <ElCheckbox v-model={checkedRiskTips.value}>{t('该理财运作项目为链上， 有本金损失风险')}</ElCheckbox>
                    </div>
                  )}
                  <div class="mt-3">
                    <ElCheckbox v-model={checkedProtocol.value}>
                      <div>
                        <span class="!text-[var(--colors-text-base)]">{t('我已阅读并同意')}</span>
                        <span class="!text-[var(--colors-primary)]" onClick={onOpenProtocol}>
                          {t('《{platformName}理财协议》', {
                            platformName: t(PlatformName),
                          })}
                        </span>
                      </div>
                    </ElCheckbox>
                  </div>
                </div>
              </div>
            ),
            footer: () => (
              <HocElDialogFooterLayout>
                <CustomButton type="primary" isFull size={'medium'} loading={submitLoading.value} disabled={confirmDisabled.value} onClick={onConfirm}>
                  {t('确认')}
                </CustomButton>
              </HocElDialogFooterLayout>
            ),
          }}
        </HocElDialog>

        <HocElDialog v-model={successDialogVisible.value} width={440} placement="bottom" showClose={false} closeOnClickModal={false}>
          {{
            header: () => <DialogHeader onClose={onCloseSuccessDialog}>{t('提示信息')}</DialogHeader>,
            default: () => (
              <div class="flex flex-col items-center justify-center">
                <img src={successIcon.value} width={60} height={60} alt="success" />
                <div class="mt-8 text-[18px] font-[500] leading-[21px]">{t('申购成功！')}</div>
                <div class="mt-3 text-[14px] font-[400] leading-[22px] text-[var(--colors-text-secondary)]">{t('本金和收益到期自动发放至现货账户')}</div>
                {isAutoPurchase.value && (
                  <div class={'px-3 flex mt-12 justify-between rounded-[8px] items-center h-[60px] w-full bg-[var(--colors-bg-main-page-card)]'}>
                    <span class={'text-[16px]'}>{t('自动申购')}</span>
                    <ElSwitch modelValue={autoPurchase.value} loading={autoLoading.value} onChange={() => onAutoPurchaseChange()} />
                  </div>
                )}
              </div>
            ),
            footer: () => (
              <HocElDialogFooterLayout>
                <div class="flex w-full">
                  <CustomButton type="default" isFull size={'medium'} onClick={onCloseSuccessDialog}>
                    {t('前往理财')}
                  </CustomButton>
                  <CustomButton type="primary" isFull size={'medium'} onClick={onQueryOrder}>
                    {t('查看订单')}
                  </CustomButton>
                </div>
              </HocElDialogFooterLayout>
            ),
          }}
        </HocElDialog>
      </>
    )
  },
})
